import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../environments';

type OneSignalQueue = Array<(oneSignal: OneSignalSdk) => void | Promise<void>>;

interface OneSignalWindow extends Window {
  OneSignalDeferred?: OneSignalQueue;
}

interface OneSignalSdk {
  init(options: Record<string, unknown>): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  Notifications: {
    permission: boolean;
    permissionNative: NotificationPermission;
    requestPermission(): Promise<boolean>;
  };
  User: {
    PushSubscription: {
      id?: string | null;
      token?: string | null;
      optedIn?: boolean;
      optIn(): Promise<void>;
      optOut(): Promise<void>;
    };
  };
}

export interface PushSubscriptionState {
  permission: NotificationPermission | 'unsupported';
  subscriptionId?: string;
  token?: string;
  optedIn: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OneSignalService {
  private readonly platformId = inject(PLATFORM_ID);
  private initialization?: Promise<OneSignalSdk | null>;
  private readonly sdkLoadTimeoutMs = 10000;

  isAvailable(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      !!environment.features?.enablePushNotifications &&
      !!environment.oneSignal?.appId &&
      'Notification' in window &&
      'serviceWorker' in navigator
    );
  }

  async initialize(): Promise<OneSignalSdk | null> {
    if (!this.isAvailable()) {
      return null;
    }

    this.initialization ??= this.loadSdk().then((oneSignal) =>
      this.run(oneSignal, async (sdk) => {
        await sdk.init({
          appId: environment.oneSignal.appId,
          safari_web_id: environment.oneSignal.safariWebId || undefined,
          serviceWorkerPath:
            environment.oneSignal.serviceWorkerPath ||
            'OneSignalSDKWorker.js',
          serviceWorkerUpdaterPath:
            environment.oneSignal.serviceWorkerUpdaterPath ||
            'OneSignalSDKUpdaterWorker.js',
          allowLocalhostAsSecureOrigin:
            environment.oneSignal.allowLocalhostAsSecureOrigin ?? false,
          notifyButton: {
            enable: false,
          },
        });
        return sdk;
      })
    );

    return this.initialization;
  }

  async login(externalUserId: string): Promise<void> {
    const sdk = await this.initialize();
    if (!sdk || !externalUserId) return;
    await this.run(sdk, (oneSignal) => oneSignal.login(externalUserId));
  }

  async logout(): Promise<void> {
    const sdk = await this.initialize();
    if (!sdk) return;
    await this.run(sdk, (oneSignal) => oneSignal.logout());
  }

  async requestPermission(): Promise<boolean> {
    const sdk = await this.initialize();
    if (!sdk) return false;

    return this.run(sdk, async (oneSignal) => {
      if (oneSignal.Notifications.permission) {
        return true;
      }
      return oneSignal.Notifications.requestPermission();
    });
  }

  async optIn(): Promise<PushSubscriptionState | null> {
    const sdk = await this.initialize();
    if (!sdk) return null;

    return this.run(sdk, async (oneSignal) => {
      await oneSignal.User.PushSubscription.optIn();
      return this.getStateFromSdk(oneSignal);
    });
  }

  async optOut(): Promise<void> {
    const sdk = await this.initialize();
    if (!sdk) return;

    await this.run(sdk, (oneSignal) =>
      oneSignal.User.PushSubscription.optOut()
    );
  }

  async getState(): Promise<PushSubscriptionState> {
    const sdk = await this.initialize();
    if (!sdk || !('Notification' in window)) {
      return {
        permission: 'unsupported',
        optedIn: false,
      };
    }

    return this.run(sdk, (oneSignal) => this.getStateFromSdk(oneSignal));
  }

  private getStateFromSdk(oneSignal: OneSignalSdk): PushSubscriptionState {
    return {
      permission: oneSignal.Notifications.permissionNative,
      subscriptionId: oneSignal.User.PushSubscription.id || undefined,
      token: oneSignal.User.PushSubscription.token || undefined,
      optedIn: !!oneSignal.User.PushSubscription.optedIn,
    };
  }

  private async loadSdk(): Promise<OneSignalSdk> {
    const oneSignalWindow = window as OneSignalWindow;
    oneSignalWindow.OneSignalDeferred ??= [];

    if (!document.getElementById('onesignal-sdk')) {
      const script = document.createElement('script');
      script.id = 'onesignal-sdk';
      script.src =
        environment.oneSignal.sdkUrl ||
        'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.defer = true;
      document.head.appendChild(script);
    }

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('OneSignal SDK loading timed out'));
      }, this.sdkLoadTimeoutMs);

      const script = document.getElementById(
        'onesignal-sdk'
      ) as HTMLScriptElement | null;

      if (script) {
        script.onerror = () => {
          window.clearTimeout(timeout);
          reject(new Error(`Unable to load OneSignal SDK from ${script.src}`));
        };
      }

      oneSignalWindow.OneSignalDeferred!.push((oneSignal) => {
        window.clearTimeout(timeout);
        resolve(oneSignal);
      });
    });
  }

  private async run<T>(
    oneSignal: OneSignalSdk,
    callback: (oneSignal: OneSignalSdk) => T | Promise<T>
  ): Promise<T> {
    const oneSignalWindow = window as OneSignalWindow;
    oneSignalWindow.OneSignalDeferred ??= [];

    return new Promise<T>((resolve, reject) => {
      oneSignalWindow.OneSignalDeferred!.push(async () => {
        try {
          resolve(await callback(oneSignal));
        } catch (error) {
          reject(error);
        }
      });
    });
  }
}
